import { LocalStorageService } from '../../services/local-storage.service';
/**
 * FeatureManagerService
 *
 * Manages registration and lifecycle of all Leaflet map features.
 */

import { CacheService } from '../../services/cache.service';
import { isSelectableFeature } from './selectable-feature';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { MapFeature } from './map-feature';
import { MapViewport, sameMapViewport } from './spatial/map-viewport';

export const MAP_FEATURE_VISIBILITY_STORAGE_KEY =
  'map-feature-visibility@1';

type PreferredEnabledByFeatureKey = Record<string, boolean>;

@Injectable({ providedIn: 'root' })
export class MapFeatureManagerService {
  constructor(
    cache: CacheService,
    private readonly storage: LocalStorageService,
  ) {
    cache.sessionChanged$.subscribe(() => {
      this.preferredEnabledByFeatureKey = this.readPreferredEnabledByFeatureKey();
      this.viewport = undefined;
      for (const feature of Object.values(this.features)) {
        feature.resetSession?.();
        if (isSelectableFeature(feature)) feature.clearSelection();
        feature.enabled = this.preferredEnabledByFeatureKey[feature.key] ?? this.defaultEnabledByFeatureKey[feature.key];
      }
      this.featuresSubject.next(Object.values(this.features));
    });
  }

  /** Registry of all registered features by key */
  private features: Record<string, MapFeature> = {};
  private readonly defaultEnabledByFeatureKey: Record<string, boolean> = {};
  private featureChanges: Record<string, Subscription> = {};
  private viewport?: MapViewport;
  /** The user's layer visibility choices, independent of Workspace navigation. */
  private preferredEnabledByFeatureKey: PreferredEnabledByFeatureKey =
    this.readPreferredEnabledByFeatureKey();

  /** Reactive stream of all registered features */
  private featuresSubject = new BehaviorSubject<MapFeature[]>([]);
  features$ = this.featuresSubject.asObservable();

  /**
   * Registers a new map feature.
   * @param feature A feature object implementing the MapFeature interface.
   */
  registerFeature(feature: MapFeature): void {
    this.featureChanges[feature.key]?.unsubscribe();
    this.defaultEnabledByFeatureKey[feature.key] ??= feature.enabled;
    const stored = this.preferredEnabledByFeatureKey[feature.key];
    if (stored !== undefined) feature.enabled = stored;
    this.preferredEnabledByFeatureKey[feature.key] = feature.enabled;
    this.features[feature.key] = feature;
    if (this.viewport) feature.onViewportChanged?.(this.viewport);
    if (feature.changed$) {
      this.featureChanges[feature.key] = feature.changed$.subscribe(() => {
        this.featuresSubject.next(Object.values(this.features));
      });
    }

    // Emit updated feature list
    this.featuresSubject.next(Object.values(this.features));
  }

  /**
   * Initializes all registered features (e.g., fetch data).
   */
  async initAll(): Promise<void> {
    const tasks = Object.values(this.features).map(async (feature) => {
      if (typeof feature.init === 'function') {
        await feature.init();
      }
    });
    await Promise.all(tasks);
  }

  /**
   * Renders all features that are enabled.
   */
  renderAll(): void {
    Object.values(this.features).forEach((feature) => {
      if (feature.enabled) {
        feature.render();
      }
    });
  }

  /**
   * Retrieves a feature by key.
   * @param key Unique feature key
   */
  getFeature(key: string): MapFeature | undefined {
    return this.features[key];
  }

  /** All registered map features, as a snapshot. */
  getFeatures(): readonly MapFeature[] {
    return Object.values(this.features);
  }

  /** Publishes one settled viewport to every registered map feature. */
  updateViewport(viewport: MapViewport): void {
    if (sameMapViewport(this.viewport, viewport)) return;
    this.viewport = viewport;
    for (const feature of Object.values(this.features)) {
      feature.onViewportChanged?.(viewport);
    }
  }

  /** Refreshes the cached data owned by features in one geometry family. */
  refreshGeometry(geometryType: MapFeature['geometryType']): void {
    for (const feature of Object.values(this.features)) {
      if (feature.geometryType !== geometryType) continue;
      feature.reload?.();
    }
  }

  /**
   * Toggles the visibility of a feature on or off.
   * @param key Feature key to toggle
   */
  toggleFeature(key: string): void {
    const feature = this.features[key];
    if (!feature) return;

    feature.enabled = !feature.enabled;
    this.preferredEnabledByFeatureKey[feature.key] = feature.enabled;
    this.storePreferredEnabledByFeatureKey();
    if (feature.enabled) {
      feature.render();
    } else {
      feature.clear();
    }

    // Emit updated feature list (to reflect enabled/disabled changes)
    this.featuresSubject.next(Object.values(this.features));
  }

  /**
   * Clears all map features from the map.
   */
  clearAll(): void {
    Object.values(this.features).forEach((feature) => feature.clear());
  }

  /**
   * Persist exactly the features currently registered. Feature keys are the
   * registry's identity; adding a feature never requires another storage list.
   */
  private storePreferredEnabledByFeatureKey(): void {
    const current = Object.fromEntries(
      Object.keys(this.features).map(key => [
        key,
        this.preferredEnabledByFeatureKey[key] ?? this.features[key].enabled,
      ]),
    );
    this.storage.set(MAP_FEATURE_VISIBILITY_STORAGE_KEY, current);
  }

  private readPreferredEnabledByFeatureKey(): PreferredEnabledByFeatureKey {
    try {
      const parsed: unknown = this.storage.get<unknown>(MAP_FEATURE_VISIBILITY_STORAGE_KEY);
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return {};
      }
      return Object.fromEntries(
        Object.entries(parsed)
          .filter((entry): entry is [string, boolean] =>
            typeof entry[1] === 'boolean'),
      );
    } catch {
      return {};
    }
  }

}
